import { useEffect, useState, useCallback, useMemo, useRef } from 'react'
import { typeidUnboxed } from 'typeid-js'
import { orpc } from '@/lib/orpc-client'
import { useProjectEvent, useProjectHub } from './presence-context'

/**
 * Live kanban state (PLAN-081): the ProjectStore rows are the truth, loaded
 * over RPC and reloaded whenever the project hub says `tasks:changed`
 * (anyone's edit, the agent, an email). Local mutations apply optimistically
 * so the board never waits on the round trip; the caller persists them
 * through the `tasks.*` procedures, whose events then reconcile everyone.
 */

export interface TaskItem {
  id: string
  title: string
  description: string
  descriptionJson: unknown | null
  status: string
  position: number
  priority: number
  tags: string[]
  // NOTE: Deprecated — kept for backward compatibility. Use assigneeIds instead.
  assigneeId: string | null
  assigneeIds: string[]
  dueDate: string | null
  commentCount: number
  attachmentCount: number
  subtaskTotal: number
  subtaskCompleted: number
  createdAt: string
  updatedAt: string
}

export interface DraggingUser {
  userId: string
  name: string
  taskId: string
  fromCol: string
}

type DbTask = {
  id: string
  title: string
  description: string | null
  descriptionJson?: unknown | null
  status: string
  columnName: string
  position: number
  priority: number
  tags: string[]
  // NOTE: Deprecated — kept for backward compatibility. Use assigneeIds instead.
  assigneeId: string | null
  assigneeIds?: string[]
  dueDate: string | null
  commentCount: number
  attachmentCount?: number
  subtaskTotal?: number
  subtaskCompleted?: number
  createdAt: string
  updatedAt: string
}

const COLUMN_NAMES = ['todo', 'in_progress', 'review', 'done']
/** Several events in a burst (create + assign + attach) become one reload. */
const RELOAD_DEBOUNCE_MS = 150

type Columns = Map<string, TaskItem[]>

function emptyColumns(): Columns {
  return new Map(COLUMN_NAMES.map((c) => [c, []]))
}

function fromDb(task: DbTask): TaskItem {
  return {
    id: task.id,
    title: task.title,
    description: task.description ?? '',
    descriptionJson: task.descriptionJson ?? null,
    status: task.status,
    position: task.position,
    priority: task.priority,
    tags: task.tags,
    assigneeId: task.assigneeId,
    assigneeIds: task.assigneeIds ?? (task.assigneeId ? [task.assigneeId] : []),
    dueDate: task.dueDate,
    commentCount: task.commentCount,
    attachmentCount: task.attachmentCount ?? 0,
    subtaskTotal: task.subtaskTotal ?? 0,
    subtaskCompleted: task.subtaskCompleted ?? 0,
    createdAt: task.createdAt,
    updatedAt: task.updatedAt,
  }
}

function columnsFromDb(dbTasks: DbTask[]): Columns {
  const columns = emptyColumns()
  for (const task of dbTasks) {
    const col = COLUMN_NAMES.includes(task.columnName) ? task.columnName : 'todo'
    columns.get(col)!.push(fromDb(task))
  }
  for (const list of columns.values()) list.sort((a, b) => a.position - b.position)
  return columns
}

/** Copy-on-write helpers so React sees a new Map/array on every change. */
function cloneColumns(columns: Columns): Columns {
  return new Map([...columns].map(([k, v]) => [k, [...v]]))
}

function findTask(columns: Columns, taskId: string): { col: string; index: number } | null {
  for (const [col, list] of columns) {
    const index = list.findIndex((t) => t.id === taskId)
    if (index !== -1) return { col, index }
  }
  return null
}

export function useTasksCollab(projectId: string) {
  const hub = useProjectHub()
  const [columns, setColumns] = useState<Columns>(emptyColumns)
  const [synced, setSynced] = useState(false)
  const [draggingUsers, setDraggingUsers] = useState<DraggingUser[]>([])
  const reloadTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  const reload = useCallback(async () => {
    try {
      const result = await orpc.tasks.list({ projectId })
      setColumns(columnsFromDb(result.items))
      setSynced(true)
    } catch (err) {
      console.error('[tasks] reload failed', err)
    }
  }, [projectId])

  useEffect(() => {
    setSynced(false)
    void reload()
  }, [reload])

  useProjectEvent(['tasks:changed'], () => {
    if (reloadTimer.current) clearTimeout(reloadTimer.current)
    reloadTimer.current = setTimeout(() => {
      reloadTimer.current = null
      void reload()
    }, RELOAD_DEBOUNCE_MS)
  })
  useEffect(
    () => () => {
      if (reloadTimer.current) clearTimeout(reloadTimer.current)
    },
    []
  )

  // Who is dragging what (hub presence).
  useEffect(() => {
    if (!hub) return
    const update = () =>
      setDraggingUsers(
        hub.presence
          .filter((p) => p.dragging)
          .map((p) => ({
            userId: p.userId,
            name: p.name,
            taskId: p.dragging!.taskId,
            fromCol: p.dragging!.fromCol,
          }))
      )
    update()
    return hub.on('presence', update)
  }, [hub])

  const addTask = useCallback(
    (
      columnName: string,
      title: string,
      data?: {
        id?: string
        description?: string
        descriptionJson?: unknown | null
        priority?: number
        tags?: string[]
        // NOTE: Deprecated — kept for backward compatibility. Use assigneeIds instead.
        assigneeId?: string | null
        assigneeIds?: string[]
        dueDate?: string | null
      }
    ) => {
      const id = data?.id ?? typeidUnboxed('tsk')
      const now = new Date().toISOString()
      setColumns((prev) => {
        const next = cloneColumns(prev)
        const list = next.get(columnName)
        if (!list) return prev
        list.push({
          id,
          title,
          description: data?.description ?? '',
          descriptionJson: data?.descriptionJson ?? null,
          status: columnName,
          position: list.length,
          priority: data?.priority ?? 0,
          tags: data?.tags ?? [],
          assigneeId: data?.assigneeId ?? null,
          assigneeIds: data?.assigneeIds ?? [],
          dueDate: data?.dueDate ?? null,
          commentCount: 0,
          attachmentCount: 0,
          subtaskTotal: 0,
          subtaskCompleted: 0,
          createdAt: now,
          updatedAt: now,
        })
        return next
      })
      return id
    },
    []
  )

  const moveTask = useCallback(
    (taskId: string, fromCol: string, toCol: string, newPosition: number) => {
      setColumns((prev) => {
        const next = cloneColumns(prev)
        const from = next.get(fromCol)
        const to = next.get(toCol)
        if (!from || !to) return prev
        const index = from.findIndex((t) => t.id === taskId)
        if (index === -1) return prev
        const [task] = from.splice(index, 1)
        to.splice(Math.min(newPosition, to.length), 0, {
          ...task,
          status: toCol,
          position: newPosition,
          updatedAt: new Date().toISOString(),
        })
        return next
      })
    },
    []
  )

  const reorderTask = useCallback((taskId: string, columnName: string, newIndex: number) => {
    setColumns((prev) => {
      const next = cloneColumns(prev)
      const list = next.get(columnName)
      if (!list) return prev
      const index = list.findIndex((t) => t.id === taskId)
      if (index === -1 || index === newIndex) return prev
      const [task] = list.splice(index, 1)
      list.splice(Math.min(newIndex, list.length), 0, {
        ...task,
        position: newIndex,
        updatedAt: new Date().toISOString(),
      })
      return next
    })
  }, [])

  const updateTask = useCallback(
    (
      taskId: string,
      fields: Partial<
        Pick<
          TaskItem,
          | 'title'
          | 'description'
          | 'descriptionJson'
          | 'priority'
          | 'tags'
          | 'assigneeId'
          | 'assigneeIds'
          | 'dueDate'
          | 'commentCount'
          | 'attachmentCount'
          | 'subtaskTotal'
          | 'subtaskCompleted'
        >
      >
    ) => {
      setColumns((prev) => {
        const where = findTask(prev, taskId)
        if (!where) return prev
        const next = cloneColumns(prev)
        const list = next.get(where.col)!
        list[where.index] = {
          ...list[where.index],
          ...fields,
          updatedAt: new Date().toISOString(),
        }
        return next
      })
    },
    []
  )

  const deleteTask = useCallback((taskId: string) => {
    setColumns((prev) => {
      const where = findTask(prev, taskId)
      if (!where) return prev
      const next = cloneColumns(prev)
      next.get(where.col)!.splice(where.index, 1)
      return next
    })
  }, [])

  const setDragging = useCallback(
    (value: { taskId: string; fromCol: string } | null) => {
      hub?.setDragging(value)
    },
    [hub]
  )

  /** Replace the board with the given DB rows (grouped by columnName). */
  const syncFromDb = useCallback((dbTasks: DbTask[]) => {
    setColumns(columnsFromDb(dbTasks))
    setSynced(true)
  }, [])

  const totalTaskCount = useMemo(() => {
    let count = 0
    for (const tasks of columns.values()) count += tasks.length
    return count
  }, [columns])

  return {
    columns,
    addTask,
    moveTask,
    reorderTask,
    updateTask,
    deleteTask,
    draggingUsers,
    setDragging,
    syncFromDb,
    totalTaskCount,
    synced,
  }
}
